import { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import { getChartTheme, detectDarkMode, observeDarkMode } from '../chartTheme';

const DEFAULT_COLORS = ['#3B82F6', '#22C55E', '#EAB308', '#EF4444', '#8B5CF6', '#EC4899', '#F97316', '#06B6D4'];

export default function DonutChart({ title, items, center_label, center_value }) {
    const chartRef = useRef(null);
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);

    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    useEffect(() => {
        if (!chartRef.current || !items || items.length === 0) return;

        const theme = getChartTheme(isDarkMode);
        const textColor = isDarkMode ? '#e5e5e5' : '#1a1a1a';
        const mutedColor = isDarkMode ? '#a0a0a0' : '#5f5e5a';

        const data = items.map((item, i) => ({
            name: item.label,
            y: item.value,
            color: item.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        }));

        const chart = Highcharts.chart(chartRef.current, {
            chart: {
                ...theme.chart,
                type: 'pie',
                height: 240,
            },
            title: { text: null },
            credits: theme.credits,
            exporting: { enabled: false },
            tooltip: {
                ...theme.tooltip,
                pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    innerSize: '60%',
                    dataLabels: { enabled: false },
                    showInLegend: true,
                    borderWidth: 0,
                },
            },
            legend: {
                enabled: true,
                layout: 'vertical',
                align: 'right',
                verticalAlign: 'middle',
                itemStyle: {
                    color: isDarkMode ? '#d1d5db' : '#374151',
                    fontSize: '13px',
                    fontWeight: '400',
                },
                itemMarginBottom: 4,
                symbolRadius: 5,
                symbolHeight: 8,
                symbolWidth: 8,
                labelFormatter: function () {
                    return this.name + '  <b>' + Math.round(this.percentage) + '%</b>';
                },
            },
            series: [{
                name: title || '',
                colorByPoint: true,
                data,
            }],
        });

        if (center_value || center_label) {
            const s = chart.series[0];
            if (s && s.center) {
                const cx = s.center[0] + chart.plotLeft;
                const cy = s.center[1] + chart.plotTop;
                if (center_value) {
                    chart.renderer.text(center_value, cx, cy)
                        .attr({ align: 'center', zIndex: 5 })
                        .css({ fontSize: '18px', fontWeight: '600', color: textColor })
                        .add();
                }
                if (center_label) {
                    chart.renderer.text(center_label, cx, cy + 16)
                        .attr({ align: 'center', zIndex: 5 })
                        .css({ fontSize: '11px', fontWeight: '400', color: mutedColor })
                        .add();
                }
            }
        }

        return () => { if (chart) chart.destroy(); };
    }, [items, isDarkMode, title, center_label, center_value]);

    if (!items || items.length === 0) return null;

    return (
        <div>
            {title && <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</div>}
            <div ref={chartRef} className="w-full" />
        </div>
    );
}
